import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, ChevronRight, User, Sparkles } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import ModuleOnboard from "@/components/dashboard/ModuleOnboard";
import MisteckLifeHub from "@/components/dashboard/MisteckLifeHub";
import MisteckLifeComingSoon from "@/components/dashboard/MisteckLifeComingSoon";
import MisteckPlayHub from "@/components/dashboard/MisteckPlayHub";
import DesktopEscape from "@/components/shell/DesktopEscape";
import { useFounderGate } from "@/lib/useFounderGate";

// STAGE 6 · PHASE 1 — Command Stack (3-Tile OLED vertical launcher).
// Client-side ONLY: no new backend routes, no MongoDB collections.
// Tiles are structural gateways that route into EXISTING functional tools.

const profileKey = (user) => `mist_module_profile_${user?.user_id || user?.handle || "anon"}`;

function readProfile(user) {
  if (Array.isArray(user?.module_profile) && user.module_profile.length) return user.module_profile;
  try {
    const raw = localStorage.getItem(profileKey(user));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch {
    return null;
  }
}

// Middle-zone (Misteck Life) owned by MisteckLifeHub.jsx.
// Bottom-zone (Misteck Play) owned by MisteckPlayHub.jsx.

export default function CommandStackS6({ user, onRouteHub, onNavigate }) {
  const { saveModuleProfile } = useSession();
  const { isFounder } = useFounderGate();
  const [needsOnboard, setNeedsOnboard] = useState(false);
  const [zone, setZone] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const z = p.get("zone");
    return z === "life" || z === "play" ? z : null;
  }); // null | 'life' | 'play'
  const [lifeTab, setLifeTab] = useState(null); // deep-link target tab for Misteck Life
  const returnTabRef = useRef(null);            // Life tab to restore after a desk closes

  // Launch an area desk from Misteck Life while PRESERVING zone context: the
  // ?zone=life URL param stays, so browser BACK / in-app back re-opens Life on
  // the exact tab the user launched from (see MyDashboard.closeLanding popstate).
  const enterDesk = (hub, fromTab) => {
    if (!hub) return;
    returnTabRef.current = fromTab || null;
    onRouteHub?.(hub);   // MyDashboard.openHub → pushState ?hub= (keeps ?zone=life)
    setZone(null);       // hide Life visually; URL zone param intentionally retained
  };

  // Open/close a zone THROUGH the URL so browser/hardware BACK collapses it one
  // level (never exits the app mid-flow) and re-hydrates on refresh.
  const openZone = (z) => {
    setZone(z);
    const p = new URLSearchParams(window.location.search);
    p.set("tab", "command"); p.set("zone", z);
    window.history.pushState({ zone: z }, "", `${window.location.pathname}?${p}`);
  };
  const closeZone = () => {
    setZone(null); setLifeTab(null);
    const p = new URLSearchParams(window.location.search);
    if (p.has("zone")) {
      p.delete("zone");
      const qs = p.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
  };

  // Keep the zone in sync with browser back/forward + the universal back-pill.
  useEffect(() => {
    const onPop = () => {
      const p = new URLSearchParams(window.location.search);
      const z = p.get("zone");
      if (z === "life" || z === "play") {
        setZone(z);
        if (z === "life" && returnTabRef.current) { setLifeTab(returnTabRef.current); returnTabRef.current = null; }
      } else {
        setZone(null);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // DESKTOP dual-pane: a top-level nav switch collapses the Life/Play zone so the
  // 2/3 pane resets cleanly. Desktop-only event → mobile untouched.
  useEffect(() => {
    const onDeskNav = () => closeZone();
    window.addEventListener("mist:desk-nav", onDeskNav);
    return () => window.removeEventListener("mist:desk-nav", onDeskNav);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link consume: ?life=<tab> auto-opens the Misteck Life zone on that tab.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const life = p.get("life");
    if (life) {
      setLifeTab(life);
      setZone("life");
      p.delete("life");
      p.set("zone", "life");
      const qs = p.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    setNeedsOnboard(!readProfile(user));
  }, [user]);

  const completeOnboard = async (modules) => {
    // Persist to the user's EXISTING users doc (device-agnostic). Cache locally too.
    try { localStorage.setItem(profileKey(user), JSON.stringify(modules)); } catch { /* storage blocked */ }
    setNeedsOnboard(false);
    try {
      await saveModuleProfile?.(modules);
      toast.success("Ecosystem configured", { description: `${modules.length} modules synced to your account.` });
    } catch {
      toast.success("Ecosystem configured", { description: `${modules.length} modules pinned (offline cache).` });
    }
  };

  return (
    <div data-testid="command-stack-s6">
      {/* 3-TILE VERTICAL STACK — natural thumb-strike zones */}
      <div className="flex flex-col gap-3">
        {/* TOP ZONE (20vh) — SECURE VAULT · flat charcoal */}
        <button
          onClick={() => onRouteHub?.("vault")}
          data-testid="stack-tile-vault"
          className="relative flex min-h-[20vh] flex-col justify-between overflow-hidden rounded-[26px] border-4 border-[#2a2a2a] bg-[#101012] p-4 text-left shadow-[5px_5px_0px_#000000] transition-transform active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-12 w-12 place-items-center rounded-xl border-2 border-gold/60 bg-white/[0.04]">
              <Lock size={22} className="text-gold" />
            </span>
            <span className="flex items-center gap-1 rounded-full border-2 border-gold/50 bg-gold/10 px-2.5 py-1 font-mono text-[8.5px] font-black uppercase tracking-[0.18em] text-gold" data-testid="vault-pin-badge">
              <Lock size={9} /> PIN Locked
            </span>
          </div>
          <div>
            <p className="font-serif text-2xl leading-none text-white">My Secure Documents</p>
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">Private encrypted storage</p>
          </div>
        </button>

        {/* MIDDLE ZONE (26vh) — MISTECK LIFE · flat slate */}
        <button
          onClick={() => openZone("life")}
          data-testid="stack-tile-life"
          className="relative flex min-h-[26vh] flex-col justify-between overflow-hidden rounded-[26px] border-2 border-black bg-[#1e293b] p-4 text-left shadow-[5px_5px_0px_#000] transition-transform active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
        >
          <div className="flex items-center justify-between">
            <span className="grid h-12 w-12 place-items-center rounded-xl border-2 border-white/40 bg-white/[0.06]">
              <User size={22} className="text-slate-200" />
            </span>
            <ChevronRight size={20} className="text-white/40" />
          </div>
          <div>
            <p className="font-serif text-2xl leading-none text-white">Misteck Life</p>
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">Profile · Leisure · Career · Wealth · Business</p>
          </div>
        </button>

        {/* BOTTOM PRIMARY ZONE (30vh) — MISTECK PLAY · flat indigo */}
        <button
          onClick={() => openZone("play")}
          data-testid="stack-tile-play"
          className="relative flex min-h-[30vh] flex-col justify-between overflow-hidden rounded-[26px] border-2 border-black bg-[#4f46e5] p-4 text-left shadow-[6px_6px_0px_#000] transition-transform active:translate-x-[3px] active:translate-y-[3px] active:shadow-none"
        >
          <div className="relative flex items-center justify-between">
            <span className="grid h-12 w-12 place-items-center rounded-xl border-2 border-white/50 bg-white/15">
              <Sparkles size={22} className="text-white" />
            </span>
            <ChevronRight size={20} className="text-white/80" />
          </div>
          <div className="relative">
            <p className="font-serif text-3xl leading-none text-white">Misteck Play</p>
            <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-indigo-100/80">Mist Creations · Games</p>
          </div>
        </button>
      </div>

      {zone === "life" && (
        <DesktopEscape z="z-[54]">
          {isFounder
            ? <MisteckLifeHub onBack={closeZone} onOpenDesk={enterDesk} onNavigate={onNavigate} initialTab={lifeTab} />
            : <MisteckLifeComingSoon onBack={closeZone} />}
        </DesktopEscape>
      )}
      {zone === "play" && (
        <DesktopEscape z="z-[54]"><MisteckPlayHub onBack={closeZone} onRouteHub={onRouteHub} /></DesktopEscape>
      )}

      {needsOnboard && <ModuleOnboard onComplete={completeOnboard} />}
    </div>
  );
}
